<template>
  <div
    id="view-referral-details-modal"
    ref="modal"
    class="modal fade sp-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="referral-details-title"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <div class="sp-kicker">
              Job order referral #{{ referralId }}
              <template v-if="referral"> · ticket #{{ referral.ticket_id }}</template>
            </div>
            <h5 id="referral-details-title" class="modal-title">
              {{ referral ? referral.type : loadError ? "Referral unavailable" : "Loading…" }}
            </h5>
            <div v-if="referral" class="sp-modal-meta">
              <span class="sp-pill" :style="pillStyle(meta)">
                <i class="bi" :class="meta.icon"></i> {{ meta.label }}
              </span>
              <span>{{ unitLabel(referral.unit) }}</span>
              <span aria-hidden="true">·</span>
              <span>Referred {{ formatDateTime(referral.created_at) }}</span>
            </div>
          </div>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading && !referral" class="sp-loading">
            <span class="spinner-border spinner-border-sm mr-1"></span> Loading referral…
          </div>
          <div v-else-if="loadError && !referral" class="sp-error m-0" role="alert">
            <i class="bi bi-exclamation-triangle"></i> {{ loadError }}
          </div>

          <template v-else-if="referral">
            <dl class="sp-facts">
              <div>
                <dt>Requested by</dt>
                <dd>{{ referral.creator_name || "—" }}</dd>
              </div>
              <div>
                <dt>Unit/Section</dt>
                <dd>{{ unitLabel(referral.unit) }}</dd>
              </div>
              <div>
                <dt>Ticket filed</dt>
                <dd>{{ formatDateTime(referral.date_requested) }}</dd>
              </div>
              <div>
                <dt>{{ isCompleted ? "Completed" : "Waiting for" }}</dt>
                <dd v-if="isCompleted">{{ formatDateTime(referral.closed_at) }}</dd>
                <dd v-else>{{ shortAge(referral.created_at) }}</dd>
              </div>
            </dl>

            <section class="sp-section">
              <h6 class="sp-section-title">Request</h6>
              <p class="sp-text">{{ referral.description }}</p>
            </section>

            <section v-if="referral.diagnosis || referral.actions_taken" class="sp-section">
              <h6 class="sp-section-title">
                Findings <span class="sp-section-note">from the support team</span>
              </h6>
              <div class="sp-findings">
                <div v-if="referral.diagnosis">
                  <div class="sp-label">Issues found</div>
                  <p class="sp-text">{{ referral.diagnosis }}</p>
                </div>
                <div v-if="referral.actions_taken">
                  <div class="sp-label">Actions taken</div>
                  <p class="sp-text">{{ referral.actions_taken }}</p>
                </div>
              </div>
            </section>

            <section class="sp-section">
              <h6 class="sp-section-title">General Services remarks</h6>
              <template v-if="!isCompleted">
                <label for="referral-remarks" class="sr-only">Remarks</label>
                <textarea
                  id="referral-remarks"
                  v-model="remarks"
                  class="form-control"
                  rows="3"
                  maxlength="2000"
                  placeholder="What was done, parts used, or what's still needed"
                ></textarea>
              </template>
              <p v-else-if="referral.remarks" class="sp-text">{{ referral.remarks }}</p>
              <p v-else class="sp-muted">No remarks were recorded.</p>
            </section>

            <div v-if="confirming" ref="panel" class="sp-panel success">
              <p class="sp-panel-title">Mark referral #{{ referral.id }} as completed?</p>
              <p>
                Ticket #{{ referral.ticket_id }} closes too, and
                {{ referral.creator_name || "the requester" }} is notified.
                <template v-if="remarksChanged">Your remarks are saved first.</template>
              </p>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="confirming = false">
                  Cancel
                </button>
                <button type="button" class="btn btn-success" :disabled="!!busy" @click="complete">
                  <span v-if="busy === 'complete'" class="spinner-border spinner-border-sm mr-1"></span>
                  Mark as completed
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button
            v-if="referral"
            type="button"
            class="btn btn-outline-secondary text-dark mr-auto"
            @click="$emit('print', printable)"
          >
            <i class="bi bi-printer mr-1"></i> Print
          </button>
          <button type="button" class="btn btn-light" data-dismiss="modal">Done</button>
          <template v-if="referral && !isCompleted && !confirming">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="!remarksChanged || !!busy"
              @click="saveRemarks"
            >
              <span v-if="busy === 'save'" class="spinner-border spinner-border-sm mr-1"></span>
              Save remarks
            </button>
            <button type="button" class="btn btn-success" :disabled="!!busy" @click="startConfirm">
              <i class="bi bi-check2-circle mr-1"></i> Mark as completed
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  UNITS,
  errorMessage,
  formatDateTime,
  pillStyle,
  referralStatusMeta,
  shortAge,
} from "../../supportOptions";

export default {
  name: "ViewJobOrderReferralDetailsModal",
  data() {
    return {
      referralId: null,
      referral: null,
      remarks: "",
      loading: false,
      loadError: "",
      confirming: false,
      busy: "",
      seq: 0,
      skipDirtyCheck: false,
    };
  },
  computed: {
    meta() {
      return referralStatusMeta(this.referral && this.referral.status);
    },
    isCompleted() {
      return !!this.referral && this.referral.status === "Closed";
    },
    remarksChanged() {
      if (!this.referral || this.isCompleted) return false;
      return this.remarks.trim() !== (this.referral.remarks || "").trim();
    },
    // The print form shows whatever is in the box, saved or not.
    printable() {
      return { ...this.referral, remarks: this.isCompleted ? this.referral.remarks : this.remarks };
    },
  },
  mounted() {
    $(this.$refs.modal)
      .on("hide.bs.modal", (event) => {
        if (event.target !== this.$refs.modal) return;
        if (this.remarksChanged && !this.skipDirtyCheck) {
          if (!window.confirm("Discard the remarks you haven't saved?")) {
            event.preventDefault();
            return;
          }
        }
        this.skipDirtyCheck = false;
      })
      .on("hidden.bs.modal", (event) => {
        if (event.target !== this.$refs.modal) return;
        this.confirming = false;
        this.$emit("hidden");
      });
  },
  beforeDestroy() {
    this.skipDirtyCheck = true;
    $(this.$refs.modal).off("hide.bs.modal hidden.bs.modal").modal("hide");
  },
  methods: {
    pillStyle,
    shortAge,
    formatDateTime,
    unitLabel(value) {
      const unit = UNITS.find((u) => u.value === value);
      return unit ? unit.label : value || "—";
    },
    /** Opens right away with the list row (if given), then loads the latest copy. */
    open(referralId, row = null) {
      this.referralId = referralId;
      this.referral = null;
      this.confirming = false;
      this.loadError = "";
      if (row) this.apply(row);
      $(this.$refs.modal).modal("show");
      this.load();
    },
    load() {
      const seq = ++this.seq;
      this.loading = true;
      return axios
        .get(`api/support/job-order-referrals/${this.referralId}`)
        .then((res) => {
          if (seq === this.seq) this.apply(res.data.referral, { keepDraft: true });
        })
        .catch((err) => {
          if (seq !== this.seq) return;
          this.loadError = errorMessage(err, "Couldn't load this referral.");
          if (this.referral) toastr.warning(this.loadError, "Showing saved copy");
        })
        .finally(() => {
          if (seq === this.seq) this.loading = false;
        });
    },
    apply(referral, { keepDraft = false } = {}) {
      // A fresh copy that arrives while someone is typing keeps their draft.
      const keep =
        keepDraft &&
        this.remarksChanged &&
        referral &&
        this.referral &&
        this.referral.id === referral.id;
      this.referral = referral || null;
      this.referralId = referral ? referral.id : this.referralId;
      if (!keep) this.remarks = (referral && referral.remarks) || "";
    },
    saveRemarks() {
      if (!this.remarksChanged || this.busy) return Promise.resolve();
      this.busy = "save";
      return axios
        .put(`api/support/job-order-referrals/${this.referral.id}`, { remarks: this.remarks })
        .then((res) => {
          toastr.success(res.data.message, "Saved");
          this.apply(res.data.referral);
          this.$emit("changed");
        })
        .catch((err) => {
          toastr.error(errorMessage(err), "Not saved");
        })
        .finally(() => {
          this.busy = "";
        });
    },
    startConfirm() {
      this.confirming = true;
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (!panel) return;
        panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
        const button = panel.querySelector(".btn-success");
        if (button) button.focus({ preventScroll: true });
      });
    },
    async complete() {
      if (this.busy) return;
      this.busy = "complete";
      try {
        if (this.remarksChanged) {
          const saved = await axios.put(`api/support/job-order-referrals/${this.referral.id}`, {
            remarks: this.remarks,
          });
          this.apply(saved.data.referral);
        }
        const res = await axios.post("api/support/job-order-referrals/close", {
          ids: [this.referral.id],
        });
        toastr.success(res.data.message, "Completed");
        this.confirming = false;
        this.$emit("changed");
        await this.load();
      } catch (err) {
        toastr.error(errorMessage(err), "Not completed");
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>
